/* eslint-disable max-classes-per-file */
import { groupBy, head } from "lodash";
import moment from "moment";
import { DateUtils } from "../utils/date-utils";
import { IAggregatPeriod } from "./aggregatperiod";
import { BufferedArraySegment } from "./arraysegment";
import { IDagKvantitet } from "./dagkvantitet";
import { LeveransraderAggregat } from "./leveransrad";
import { TaktCalculator } from "./takt-calculator";

export interface IForsorjningskalla {
    id: number;
    aktiv: boolean;
    typ: string;
    group: string;
    groupNamn: string;
    kvantitet: number;
    namn: string;
    kund: string;
    sortiment: string;
    mottagningsplats: string;
    kundorderId: number;
    leverantorsorderId: number;
    periodFrom: Date;
    periodTom: Date;
    sekvensnummer: number;
    manuellPaArbetsorder: boolean;
}

export class Forsorjningskalla implements IForsorjningskalla {
    id: number;
    aktiv: boolean;
    typ: string;
    group: string;
    groupNamn: string;
    kvantitet: number;
    namn: string;
    kund: string;
    sortiment: string;
    mottagningsplats: string;
    kundorderId: number;
    leverantorsorderId: number;
    periodFrom: Date;
    periodTom: Date;
    sekvensnummer: number;
    manuellPaArbetsorder: boolean;

    constructor(namn = "") {
        this.namn = namn;
    }
}

export class ForsorjningskallaTyp {

    // eslint-disable-next-line sonarjs/public-static-readonly
    static Produktionsomrade = "PRODUKTIONSOMRADE";

    // eslint-disable-next-line sonarjs/public-static-readonly
    static Terminal = "TERMINAL";

    // eslint-disable-next-line sonarjs/public-static-readonly
    static Leverantorsorder = "LEVERANTORSORDER";
}

export interface IForsorjningsrad {
    id?: number;
    forsorjningskallaId: number;
    typ: string;
    andel: number;
    dagkvantitet: number;
    transporteratKvantitet: number;
    avtaladDagKvantitet: number;
    rowVersion: string;
}

export interface IForsorjningsradEx extends IForsorjningsrad {
    leveransradDatum: Date;
}

export class ForsorjningskallaAggregat {

    private forsorjningsraderBuffer: BufferedArraySegment<IForsorjningsradEx>;

    constructor(private readonly raw: IForsorjningskalla, private readonly forsorjningsrader: IForsorjningsradEx[]) {
        this.initForsorjningsrader();
    }

    // eslint-disable-next-line sonarjs/public-static-readonly
    static createMany = (forsorjningskallor: IForsorjningskalla[], leveransrader: LeveransraderAggregat): ForsorjningskallaAggregat[] => {

        const forsorjningskallaForsorjningsrader = groupBy(leveransrader.toForsorjningsRader(), item => `${item.typ}-${item.forsorjningskallaId}`);

        return forsorjningskallor.map(fk => {
            const key = `${fk.group}-${fk.id}`;
            return new ForsorjningskallaAggregat(fk, forsorjningskallaForsorjningsrader[key] || []);
        });
    };

    // eslint-disable-next-line sonarjs/public-static-readonly
    static create = (forsorjningskalla: IForsorjningskalla, leveransrader: LeveransraderAggregat): ForsorjningskallaAggregat => {
        const forsorjningsrader = leveransrader
            .toForsorjningsRader()
            .filter(fr => fr.forsorjningskallaId === forsorjningskalla.id && fr.typ === forsorjningskalla.group);

        return new ForsorjningskallaAggregat(forsorjningskalla, forsorjningsrader);
    };

    get typ(): string {
        return this.raw.group;
    }

    get id(): number {
        return this.raw.id;
    }

    get namn(): string {
        return this.raw.namn.split(",")[0];
    }

    get huvudkod(): string {
        return this.raw.namn.split(",")[1];
    }

    get overenskommet(): number {
        return this.raw.kvantitet;
    }

    set overenskommet(value: number) {
        this.raw.kvantitet = value;
    }

    get kundorderId(): number {
        return this.raw.kundorderId;
    }

    get leverantorsorderId(): number {
        return this.raw.leverantorsorderId;
    }

    get periodFrom(): Date {
        return this.raw.periodFrom;
    }

    get periodTom(): Date {
        return this.raw.periodTom;
    }

    get mottagningsplats(): string {
        return this.raw.mottagningsplats;
    }

    get sortiment(): string {
        return this.raw.sortiment;
    }

    get manuellPaArbetsorder(): boolean {
        return this.raw.manuellPaArbetsorder;
    }

    set manuellPaArbetsorder(value: boolean) {
        this.raw.manuellPaArbetsorder = value;
    }

    sumAvtalat = (period: IAggregatPeriod): number => this.forsorjningsraderBuffer.periodSegment(period).reduce((agg, item) => agg + item.avtaladDagKvantitet, 0);

    sumTransporterat = (period: IAggregatPeriod): number => this.forsorjningsraderBuffer.periodSegment(period).reduce((agg, item) => agg + item.transporteratKvantitet, 0);

    sumPlan = (period: IAggregatPeriod): number => this.forsorjningsraderBuffer.periodSegment(period).reduce((agg, item) => agg + item.dagkvantitet, 0);

    sumTransporteratPlanerat = (periods: IAggregatPeriod[]): number =>
        periods.reduce((agg, item) => agg + (item.isCurrentlyActive ? this.sumPlan(item) : this.sumTransporterat(item)), 0);

    avvikelse = (periods: IAggregatPeriod[]): number => this.sumTransporteratPlanerat(periods) - this.overenskommet;

    calculateTakt = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumPlan, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);

    updateForsorjningsradAvtalad = (fordeladKvantitet: IDagKvantitet[]): void => {
        const forsorjningsraderPerDag = groupBy(this.forsorjningsrader, x => moment(x.leveransradDatum).format(DateUtils.dateformat));

        fordeladKvantitet.forEach(dagkvantitet => {
            const datum = moment(dagkvantitet.datum).format(DateUtils.dateformat);

            let forsorjningsrad = head(forsorjningsraderPerDag[datum]);
            if (!forsorjningsrad) {
                forsorjningsrad = new DefaultForsorjningsrad(this.id, this.typ, dagkvantitet.datum);
                forsorjningsrad.dagkvantitet = 0;
                this.forsorjningsrader.push(forsorjningsrad);
            }

            forsorjningsrad.avtaladDagKvantitet = dagkvantitet.kvantitet;
        });

        this.initForsorjningsrader();
    };

    updateForsorjningsradPlan = (fordeladKvantitet: IDagKvantitet[]): void => {
        const forsorjningsraderPerDag = groupBy(this.forsorjningsrader, x => moment(x.leveransradDatum).format(DateUtils.dateformat));

        fordeladKvantitet.forEach(dagkvantitet => {
            const datum = moment(dagkvantitet.datum).format(DateUtils.dateformat);

            let forsorjningsrad = head(forsorjningsraderPerDag[datum]);
            if (!forsorjningsrad) {
                forsorjningsrad = new DefaultForsorjningsrad(this.id, this.typ, dagkvantitet.datum);
                forsorjningsrad.avtaladDagKvantitet = 0;
                this.forsorjningsrader.push(forsorjningsrad);
            }

            forsorjningsrad.dagkvantitet = dagkvantitet.kvantitet;
        });

        this.initForsorjningsrader();
    };

    private readonly initForsorjningsrader = () => {
        this.forsorjningsraderBuffer = new BufferedArraySegment<IForsorjningsradEx>(this.forsorjningsrader, x => x.leveransradDatum);
    };
}

export class DefaultForsorjningsrad implements IForsorjningsradEx {
    leveransradDatum: Date;
    id?: number;
    forsorjningskallaId: number;
    typ: string;
    andel: number;
    dagkvantitet: number;
    avtaladDagKvantitet: number;
    transporteratKvantitet: number;
    rowVersion: string;

    constructor(forsorjningskallaId: number, typ: string, datum: Date, dagkvantitet?: number) {
        this.dagkvantitet = dagkvantitet || 0;
        this.transporteratKvantitet = 0;
        this.avtaladDagKvantitet = 0;
        this.forsorjningskallaId = forsorjningskallaId;
        this.typ = typ;
        this.leveransradDatum = new Date(datum);
    }
}
